import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TDocumentSelectProps } from '@/console/features/documents/components/document-select/document-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadDocumentsForSelectQuery } from '@/shared/redux/api/assets/documents/documents-api';
import {
  DocumentForSelectFragment,
  ReadDocumentsForSelectQuery,
} from '@/shared/types/types.generated';

export const DocumentSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  createOption,
  getItemId,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  documentFilter,
  additionalOptions = [],
  onSelectChange,
  required,
}: TDocumentSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (document: DocumentForSelectFragment) => {
    return {
      label: document.name,
      value: document.documentId,
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadDocumentsForSelectQuery) => {
    if (!data) {
      return [];
    }

    const documents = data?.readAllDocuments?.items;
    const validDocuments = documentFilter
      ? documents?.filter((document) => documentFilter(document))
      : documents;

    const options = validDocuments.map((document) => _createOption(document));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<
      TFieldValues,
      ReadDocumentsForSelectQuery,
      TOptionShape
    >
      required={required}
      name={name}
      label={t('documents:select_document')}
      multiple
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadDocumentsForSelectQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
    />
  );
};
